import { createExperimentalZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { SteppedDialogFormProps, useSteppedDialogForm, useSteppedDialogFormControls } from '@mews-commander/form';
import { OptimusProvider } from '@mews-commander/ui';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { useCurrentService } from '../queries/useCurrentService';
import {
    ProductFormValues,
    useProductCreateFormDefaultValues,
    useToProductAddParameters,
} from './schemas/productFormSchema';
import { useDetailsSchema } from './schemas/detailsSchema';
import { productPricingSchema } from './schemas/pricingSchema';
import { productOptionsSchema } from './schemas/optionsSchema';
import { useAccountingSchema } from './schemas/accountingSchema';
import { ProductFormStep } from './types';
import { DetailsStepFields } from './steps/DetailsStepFields';
import { AccountingStepFields } from './steps/AccountingStepFields';
import { PricingStepFields } from './steps/PricingStepFields';
import { OptionsStepFields } from './steps/OptionsStepFields';

export const useProductDialogForm = () =>
    useSteppedDialogForm<ProductFormStep>({
        id: 'productForm',
        title: lt('CreateProduct'),
        steps: ['details', 'pricing', 'options', 'accounting'],
        saveLabel: lt('Create'),
        stepLabels: {
            details: lt('DetailPlural'),
            pricing: lt('Pricing'),
            options: lt('Options'),
            accounting: lt('Accounting'),
        },
        renderForm: (props) => (
            <OptimusProvider density="spacious">
                <ProductForm {...props} />
            </OptimusProvider>
        ),
    });

const ProductForm = (props: SteppedDialogFormProps<ProductFormStep>) => {
    const service = useCurrentService();

    const detailsSchema = useDetailsSchema();
    const accountingSchema = useAccountingSchema();

    const mutation = useRepositoryMutation('products', 'add', {
        invalidateQueries: { products: ['getAll', 'getOverviewEntries'] },
        onSuccess: () => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        },
    });
    const toProductAddParameters = useToProductAddParameters();

    const { renderFormContent } = useSteppedDialogFormControls<ProductFormStep, ProductFormValues>({
        ...props,
        stepResolvers: {
            details: createExperimentalZodFormResolver(detailsSchema),
            pricing: createExperimentalZodFormResolver(productPricingSchema),
            options: createExperimentalZodFormResolver(productOptionsSchema),
            accounting: createExperimentalZodFormResolver(accountingSchema),
        },
        defaultValues: useProductCreateFormDefaultValues(),
        onSubmit: async (formValues) => {
            const requestParameters = await toProductAddParameters(formValues);
            await mutation.mutateAsync({ serviceId: service.id, products: [requestParameters] });
        },
    });

    return renderFormContent((step) => {
        switch (step) {
            case 'details':
                return (
                    <DetailsStepFields
                        helpers={{
                            descriptions: null,
                            externalIdentifier: lt('ProductFormInputHintExternalIdentifier'),
                            image: lt('ProductFormInputHintImage'),
                            names: lt('ProductFormInputHintName'),
                        }}
                    />
                );
            case 'pricing':
                return <PricingStepFields />;
            case 'options':
                return <OptionsStepFields />;
            case 'accounting':
                return <AccountingStepFields />;
        }
    });
};
